<script setup>
import { ref, computed } from 'vue'

// 频率输入和单位
const frequency = ref(7.1)
const frequencyUnit = ref('MHz')
const activeBand = ref(null) // 当前选中的频段

// 波长计算结果
const wavelength = computed(() => {
  // 光速 (m/s)
  const c = 299792458

  // 将频率转换为Hz
  let freqHz = frequency.value
  switch (frequencyUnit.value) {
    case 'kHz':
      freqHz *= 1000
      break
    case 'MHz':
      freqHz *= 1000000
      break
    case 'GHz':
      freqHz *= 1000000000
      break
  }

  // 计算波长 (米)
  const lambda = c / freqHz

  return {
    meters: lambda,
    feet: lambda * 3.28084,
    inches: lambda * 39.3701
  }
})

// 天线长度计算
const antennaLengths = computed(() => {
  const lambda = wavelength.value.meters

  return {
    // 半波长偶极子 (每臂长度)
    dipoleArm: lambda * 0.25 * 0.95, // 95% 速度因子

    // 全波长环形天线
    fullWaveLoop: lambda * 1.0 * 0.95,

    // 5/8 波长垂直天线
    fiveEighthsVertical: lambda * 0.625 * 0.95,

    // 1/4 波长垂直天线
    quarterWaveVertical: lambda * 0.25 * 0.95,

    // J极天线
    jPole: {
      vertical: lambda * 0.75 * 0.95,
      stub: lambda * 0.25 * 0.95
    }
  }
})

// 格式化数字，保留2位小数
function formatNumber(num) {
  return Number(num).toFixed(2)
}

// 频率范围
const bands = [
  { name: '160米', range: '1.8-2.0 MHz', center: 1.9 },
  { name: '80米', range: '3.5-4.0 MHz', center: 3.75 },
  { name: '40米', range: '7.0-7.3 MHz', center: 7.15 },
  { name: '30米', range: '10.1-10.15 MHz', center: 10.125 },
  { name: '20米', range: '14.0-14.35 MHz', center: 14.175 },
  { name: '17米', range: '18.068-18.168 MHz', center: 18.118 },
  { name: '15米', range: '21.0-21.45 MHz', center: 21.225 },
  { name: '12米', range: '24.89-24.99 MHz', center: 24.94 },
  { name: '10米', range: '28.0-29.7 MHz', center: 28.85 },
  { name: '6米', range: '50-54 MHz', center: 52 },
  { name: '2米', range: '144-148 MHz', center: 146 },
  { name: '70厘米', range: '430-440 MHz', center: 435 }
]

// 设置预设频率
function setPresetFrequency(freq, band) {
  frequency.value = freq
  frequencyUnit.value = freq < 1 ? 'kHz' : (freq > 1000 ? 'GHz' : 'MHz')
  activeBand.value = band // 设置当前选中的频段
}

// 检查频段是否为当前选中的频段
function isActiveBand(band) {
  return activeBand.value === band
}
</script>

<template>
  <div class="frequency-calculator">
    <h2>频率计算器</h2>
    <p>计算无线电频率对应的波长和常用天线尺寸。</p>

    <div class="input-container">
      <label for="frequency-input">频率:</label>
      <div class="input-field-group">
        <input
          type="number"
          id="frequency-input"
          v-model.number="frequency"
          min="0.001"
          step="0.001"
        >
        <select v-model="frequencyUnit">
          <option value="kHz">kHz</option>
          <option value="MHz">MHz</option>
          <option value="GHz">GHz</option>
        </select>
      </div>
    </div>

    <h4>常用频段:</h4>
    <div class="band-buttons">
      <button
        v-for="band in bands"
        :key="band.name"
        @click="setPresetFrequency(band.center, band)"
        :class="['band-button', { 'active': isActiveBand(band) }]"
      >
        {{ band.name }}
      </button>
    </div>

    <div class="results-section">
      <div class="result-group">
        <h3>波长</h3>
        <div class="result-item">
          <div class="result-label">米:</div>
          <div class="result-value">{{ formatNumber(wavelength.meters) }} m</div>
        </div>
        <div class="result-item">
          <div class="result-label">英尺:</div>
          <div class="result-value">{{ formatNumber(wavelength.feet) }} ft</div>
        </div>
        <div class="result-item">
          <div class="result-label">英寸:</div>
          <div class="result-value">{{ formatNumber(wavelength.inches) }} in</div>
        </div>
      </div>

      <div class="result-group">
        <h3>天线尺寸</h3>
        <div class="result-item">
          <div class="result-label">半波长偶极子 (每臂):</div>
          <div class="result-value">{{ formatNumber(antennaLengths.dipoleArm) }} m</div>
        </div>
        <div class="result-item">
          <div class="result-label">1/4 波长垂直天线:</div>
          <div class="result-value">{{ formatNumber(antennaLengths.quarterWaveVertical) }} m</div>
        </div>
        <div class="result-item">
          <div class="result-label">5/8 波长垂直天线:</div>
          <div class="result-value">{{ formatNumber(antennaLengths.fiveEighthsVertical) }} m</div>
        </div>
        <div class="result-item">
          <div class="result-label">全波长环形天线:</div>
          <div class="result-value">{{ formatNumber(antennaLengths.fullWaveLoop) }} m</div>
        </div>
      </div>

      <div class="result-group">
        <h3>J极天线</h3>
        <div class="result-item">
          <div class="result-label">垂直部分:</div>
          <div class="result-value">{{ formatNumber(antennaLengths.jPole.vertical) }} m</div>
        </div>
        <div class="result-item">
          <div class="result-label">匹配段:</div>
          <div class="result-value">{{ formatNumber(antennaLengths.jPole.stub) }} m</div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* 新的输入容器样式 */
.input-container {
  margin-bottom: 15px;
  display: flex;
  flex-direction: column;
}

.input-container label {
  margin-bottom: 8px;
  font-weight: 500;
  color: #444;
}

.input-field-group {
  display: flex;
  align-items: center;
  width: 100%;
}

.input-field-group input {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px 0 0 4px;
  font-size: 16px;
  min-width: 0; /* 允许输入框缩小 */
}

.input-field-group select {
  width: 80px;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-left: none;
  border-radius: 0 4px 4px 0;
  font-size: 16px;
  background-color: white;
}

.input-field-group input:focus, .input-field-group select:focus {
  outline: none;
  border-color: #3a8c3a;
  box-shadow: 0 0 0 2px rgba(58, 140, 58, 0.2);
}

/* 移动端适配 */
@media (max-width: 600px) {
  .input-field-group input {
    padding: 8px 10px;
    font-size: 14px;
  }

  .input-field-group select {
    width: 70px;
    padding: 8px 10px;
    font-size: 14px;
  }
}

/* 超小屏幕优化 */
@media (max-width: 400px) {
  .input-field-group input {
    padding: 6px 8px;
    font-size: 13px;
  }

  .input-field-group select {
    width: 60px;
    padding: 6px 8px;
    font-size: 13px;
  }
}

/* 保留原有的其他样式 */
.frequency-calculator {
  max-width: 800px;
  margin: 0 auto;
}

h2 {
  color: #333;
  margin-bottom: 10px;
}

p {
  color: #666;
  margin-bottom: 20px;
  line-height: 1.5;
}

h4 {
  margin-top: 20px;
  margin-bottom: 12px;
  color: #3a8c3a;
  font-size: 1rem;
}

/* 移动端适配 - 保持水平布局 */
@media (max-width: 600px) {
  .input-group {
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-bottom: 15px;
    flex-wrap: nowrap; /* 防止换行 */
  }

  .input-group label {
    min-width: 50px; /* 进一步减小标签宽度 */
    margin-right: 8px;
    font-size: 14px;
    white-space: nowrap;
  }

  .input-group input {
    padding: 8px 10px;
    font-size: 14px;
    flex: 1;
    min-width: 0; /* 关键属性，允许输入框缩小 */
  }

  .input-group select {
    padding: 8px 10px;
    font-size: 14px;
    margin-left: 8px;
    min-width: 70px;
    flex-shrink: 0; /* 防止下拉框缩小 */
  }
}

/* 超小屏幕优化 */
@media (max-width: 400px) {
  .input-group label {
    min-width: 40px; /* 进一步减小标签宽度 */
    font-size: 13px;
    margin-right: 5px;
  }

  .input-group input {
    padding: 6px 8px;
    font-size: 13px;
  }

  .input-group select {
    padding: 6px 8px;
    font-size: 13px;
    margin-left: 5px;
    min-width: 60px; /* 减小最小宽度 */
  }
}

/* 修复响应式调整中的冲突 */
@media (max-width: 600px) {
  .results-section {
    grid-template-columns: 1fr;
  }

  /* 移除这些冲突的样式 */
  /*.input-group {
    flex-direction: column;
    align-items: flex-start;
  }

  .input-group label {
    margin-bottom: 8px;
  }

  .input-group input, .input-group select {
    width: 100%;
    margin-left: 0;
    margin-top: 5px;
  }*/
}

.band-buttons {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  gap: 8px;
  margin-bottom: 25px;
}

.band-button {
  padding: 8px 5px;
  font-size: 14px;
  text-align: center;
  white-space: nowrap;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  background-color: #f0f0f0;
  color: #333;
  border: 1px solid #ddd;
  border-radius: 4px;
  transition: all 0.3s;
}

.band-button:hover {
  background-color: #e0e0e0;
}

/* 选中的频段按钮样式 */
.band-button.active {
  background-color: #4caf50;
  color: white;
  border-color: #388e3c;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.results-section {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
  margin-top: 25px;
}

.result-group {
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 18px;
  background-color: #f9f9f9;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.result-group:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
}

.result-group h3 {
  color: #3a8c3a;
  margin-top: 0;
  margin-bottom: 15px;
  font-size: 1.2rem;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 10px;
  text-align: center;
}

.result-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  padding: 8px 0;
  border-bottom: 1px dashed #eee;
}

.result-item:last-child {
  margin-bottom: 0;
  border-bottom: none;
}

.result-label {
  color: #555;
  font-weight: 500;
}

.result-value {
  font-weight: 700;
  color: #333;
  background-color: #e8f5e9;
  padding: 3px 8px;
  border-radius: 4px;
  min-width: 80px;
  text-align: center;
}

/* 波长结果组特殊颜色 */
.result-group:nth-child(1) {
  border-top: 4px solid #4caf50;
}

.result-group:nth-child(1) h3 {
  color: #2e7d32;
}

.result-group:nth-child(1) .result-value {
  background-color: #e8f5e9;
}

/* 天线尺寸结果组特殊颜色 */
.result-group:nth-child(2) {
  border-top: 4px solid #2196f3;
}

.result-group:nth-child(2) h3 {
  color: #1565c0;
}

.result-group:nth-child(2) .result-value {
  background-color: #e3f2fd;
}

/* J极天线结果组特殊颜色 */
.result-group:nth-child(3) {
  border-top: 4px solid #ff9800;
}

.result-group:nth-child(3) h3 {
  color: #ef6c00;
}

.result-group:nth-child(3) .result-value {
  background-color: #fff3e0;
}

/* 深色模式适配 */
@media (prefers-color-scheme: dark) {
  h2, .result-value {
    color: #e0e0e0;
  }

  p {
    color: #bbb;
  }

  h4 {
    color: #7cb342;
  }

  /* 输入框和选择器深色模式 */
  .input-field-group input {
    background-color: #333;
    color: #e0e0e0;
    border-color: #444;
  }

  .input-field-group select {
    background-color: #333;
    color: #e0e0e0;
    border-color: #444;
  }

  .input-field-group input:focus,
  .input-field-group select:focus {
    border-color: #7cb342;
    box-shadow: 0 0 0 2px rgba(124, 179, 66, 0.2);
  }

  .input-container label {
    color: #bbb;
  }

  /* 常用频段按钮深色模式 */
  .band-button {
    background-color: #333;
    color: #e0e0e0;
    border-color: #444;
  }

  .band-button:hover {
    background-color: #444;
    transform: translateY(-2px);
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  }

  /* 选中的频段按钮样式 */
  .band-button.active {
    background-color: #4caf50;
    color: #fff;
    border-color: #388e3c;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3);
  }

  .result-group {
    background-color: #1e1e1e;
    border-color: #444;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  }

  .result-group h3 {
    border-bottom-color: #444;
  }

  .result-item {
    border-bottom-color: #333;
  }

  .result-label {
    color: #bbb;
  }

  .result-value {
    color: #e0e0e0;
  }

  /* 波长结果组深色模式 */
  .result-group:nth-child(1) {
    border-top-color: #4caf50;
  }

  .result-group:nth-child(1) h3 {
    color: #81c784;
  }

  .result-group:nth-child(1) .result-value {
    background-color: rgba(76, 175, 80, 0.2);
  }

  /* 天线尺寸结果组深色模式 */
  .result-group:nth-child(2) {
    border-top-color: #2196f3;
  }

  .result-group:nth-child(2) h3 {
    color: #64b5f6;
  }

  .result-group:nth-child(2) .result-value {
    background-color: rgba(33, 150, 243, 0.2);
  }

  /* J极天线结果组深色模式 */
  .result-group:nth-child(3) {
    border-top-color: #ff9800;
  }

  .result-group:nth-child(3) h3 {
    color: #ffb74d;
  }

  .result-group:nth-child(3) .result-value {
    background-color: rgba(255, 152, 0, 0.2);
  }
}
</style>







